import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  AlertTriangle,
  BrainCircuit, 
  MessageSquare, 
  Database,
  User,
  X,
  FileText,
  Workflow,
  CheckCircle,
  Zap,
  Shield,
  MonitorPlay,
  ListTree,
  LayoutGrid,
  ArrowRight,
  Code,
  Users
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ContactForm from '../ContactForm';
import ParticlesBackground from '../components/ParticlesBackground';
import LanguageSwitcher from '../components/LanguageSwitcher';

// Header Component (Reused to maintain consistency)
const PageHeader = ({ onOpenPopup, t }) => (
  <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
      <a href="/" className="flex items-center">
        <img
          src="https://imgur.com/nYWIvWJ.png"
          alt="Nextcoders Logo"
          className="h-8"
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; }}
        />
      </a>
      <div className="flex items-center gap-4 md:gap-8">
        <LanguageSwitcher variant="compact" />
        <button
          onClick={onOpenPopup}
          className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold"
        >
          {t('sdi.header_cta')}
        </button>
      </div>
    </div>
  </header>
);

const SdiPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const heroImageUrl = '/images/sdi/sdi.png';

  const workflowSteps = useMemo(() => t('sdi.workflow.steps', { returnObjects: true }), [t]);
  const userDrivenItems = useMemo(() => t('sdi.user_driven.items', { returnObjects: true }), [t]);
  const mockup = useMemo(() => t('sdi.user_driven.mockup', { returnObjects: true }), [t]);
  const deliverables = useMemo(() => t('sdi.deliverables_ext.items', { returnObjects: true }), [t]);
  const serviceLevels = useMemo(() => t('sdi.service_levels', { returnObjects: true }), [t]);
  const techCapabilities = useMemo(() => t('sdi.tech_capabilities.items', { returnObjects: true }), [t]);

  const openPopup = () => {
    setIsPopupOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closePopup = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = 'auto';
  };

  // Componente para tarjetas de flujo
  const FlowCard = ({ step, title, description, icon: Icon, colorClass }) => (
    <div className="relative bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center text-center h-full hover:shadow-lg transition-shadow duration-300">
      <div className={`absolute -top-4 bg-gray-900 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold shadow-md`}>
        {step}
      </div>
      <div className={`mb-4 p-4 rounded-full ${colorClass.bg} ${colorClass.text}`}>
        <Icon size={32} />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );

  // Componente para tarjetas de User-Driven
  const FeatureCard = ({ icon: Icon, title, description, colorClass }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-6 hover:border-blue-300 transition-colors">
      <div className="flex items-center gap-3 mb-3">
        <Icon className={colorClass} size={24} />
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
      </div>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  );

  return (
    <div className="relative min-h-screen bg-transparent font-sans text-gray-800 selection:bg-blue-100">
      <ParticlesBackground />
      
      {/* Popup Modal */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex justify-center items-center p-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl p-8 max-w-lg w-full relative transform transition-all duration-300 scale-100">
            <button
              onClick={closePopup}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-800 transition"
              aria-label={t('sdi.popup.close')}
            >
              <X size={24} />
            </button>
            <div className="text-center">
              <h3 className="text-2xl font-bold text-[#141414] mb-3">{t('sdi.popup.title')}</h3>
              <p className="text-gray-600 mb-6">{t('sdi.popup.desc')}</p>
              <ContactForm
                formData="inline/4/10oz6w"
                formId="4"
                scriptSrc="https://cdn.bitrix24.es/b24284919/crm/form/loader_4.js"
              />
            </div>
          </div>
        </div>
      )}

      <PageHeader onOpenPopup={openPopup} t={t} />

      <main className="pb-20">
        <section className="relative w-screen left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] mb-20">
          <div className="relative overflow-hidden bg-[#0B0B0B] min-h-screen">
            <div className="absolute top-0 left-0 right-0 z-20">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-24">
                <button
                  type="button"
                  onClick={() => {
                    if (window.history.length > 1) navigate(-1);
                    else navigate('/');
                  }}
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white transition font-semibold bg-white/10 border border-white/15 backdrop-blur px-4 py-2 rounded-full"
                >
                  <ArrowLeft size={16} />
                  {t('sdi.back')}
                </button>
              </div>
            </div>

            <div className="absolute inset-0">
              <img
                src={heroImageUrl}
                alt={t('sdi.hero_alt')}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/2400x1600/0b0b0b/ffffff?text=Portada'; }}
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/35 to-black/10" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            </div>

            <div className="relative z-10 p-8 md:p-14 min-h-screen flex flex-col justify-end max-w-7xl mx-auto">
              <div className="inline-flex items-center gap-3 bg-white/10 text-white px-4 py-2 rounded-full w-fit mb-6 backdrop-blur border border-white/15">
                <Code size={18} />
                <span className="text-sm font-semibold">{t('sdi.badge')}</span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white leading-[1.05] tracking-tight max-w-5xl">
                {t('sdi.title')}
              </h1>

              <p className="mt-6 text-xl md:text-2xl text-white/90 max-w-4xl font-medium">
                {t('sdi.subtitle')}
              </p>

              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <button
                  onClick={openPopup}
                  className="bg-white text-[#141414] hover:bg-gray-100 font-bold py-3 px-8 rounded-full transition"
                >
                  {t('common.free_diagnostic')}
                </button>
                <a
                  href="#flujo"
                  className="bg-transparent border border-white/40 text-white hover:bg-white/10 font-bold py-3 px-8 rounded-full transition text-center"
                >
                  {t('sdi.cta_methodology')}
                </a>
              </div>
            </div>
          </div>
        </section>

        <div className="px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">

          {/* 1. Flujo de Trabajo */}
          <section id="flujo" className="mb-24">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-[#141414]">{t('sdi.workflow.title')}</h2>
              <p className="text-gray-500 mt-2">{t('sdi.workflow.subtitle')}</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative">
              {/* Conectores visuales para desktop */}
              <div className="hidden md:block absolute top-1/2 left-1/3 w-1/3 h-1 bg-gray-200 -z-10 transform -translate-y-1/2"></div>
              
              {workflowSteps.map((step, i) => (
                <FlowCard
                  key={step.title || i}
                  step={String(i + 1)}
                  title={step.title}
                  description={step.desc}
                  icon={[FileText, BrainCircuit, Zap][i] || Workflow}
                  colorClass={[{ bg: 'bg-blue-100', text: 'text-blue-600' }, { bg: 'bg-purple-100', text: 'text-purple-600' }, { bg: 'bg-green-100', text: 'text-green-600' }][i] || { bg: 'bg-gray-100', text: 'text-gray-600' }}
                />
              ))}
            </div>
          </section>

          {/* 2. Metodología User-Driven */}
          <section className="mb-24 bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100">
            <div className="flex flex-col md:flex-row gap-12 items-center">
              <div className="md:w-1/2">
                <h2 className="text-3xl font-bold text-[#141414] mb-6">{t('sdi.user_driven.title')}</h2>
                <p className="text-lg text-gray-600 mb-6">
                  {t('sdi.user_driven.subtitle')}
                </p>
                <div className="space-y-4">
                  {userDrivenItems.map((item, i) => (
                    <FeatureCard
                      key={item.title || i}
                      icon={[MessageSquare, ArrowRight, Users][i] || MessageSquare}
                      title={item.title}
                      description={item.desc}
                      colorClass={['text-indigo-600', 'text-teal-600', 'text-orange-600'][i] || 'text-blue-600'}
                    />
                  ))}
                </div>
              </div>
              <div className="md:w-1/2 flex justify-center">
                <div className="bg-gradient-to-br from-slate-950 to-slate-900 rounded-2xl shadow-2xl text-white max-w-lg w-full border border-white/10 overflow-hidden">
                  <div className="px-6 pt-6 pb-5 border-b border-white/10">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <div className="text-sm font-semibold text-indigo-200">{mockup.title}</div>
                        <div className="text-xs text-white/60 mt-1">{mockup.subtitle}</div>
                      </div>
                      <div className="text-xs font-semibold px-3 py-1 rounded-full bg-teal-500/15 text-teal-200 border border-teal-500/20">
                        {mockup.live}
                      </div>
                    </div>
                  </div>

                  <div className="p-6 space-y-4">
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-xl bg-indigo-500/15 border border-indigo-500/20 text-indigo-200">
                          <CheckCircle size={18} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-white/60">{mockup.alert1.label}</div>
                          <div className="text-sm font-semibold text-white/90 mt-1">{mockup.alert1.title}</div>
                          <div className="text-xs text-white/55 mt-2">{mockup.alert1.desc}</div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <button className="px-3 py-1.5 rounded-xl text-[11px] font-semibold bg-indigo-500/25 border border-indigo-500/30 text-indigo-200 hover:bg-indigo-500/30 transition">
                          {mockup.alert1.btn_ok}
                        </button>
                        <button className="px-3 py-1.5 rounded-xl text-[11px] font-semibold bg-orange-500/20 border border-orange-500/30 text-orange-200 hover:bg-orange-500/25 transition">
                          {mockup.alert1.btn_no}
                        </button>
                      </div>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-xl bg-orange-500/15 border border-orange-500/20 text-orange-200">
                          <AlertTriangle size={18} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-white/60">{mockup.alert2.label}</div>
                          <div className="text-sm font-semibold text-white/90 mt-1">{mockup.alert2.title}</div>
                          <div className="text-xs text-white/55 mt-2">{mockup.alert2.desc}</div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <div className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-orange-500/10 border border-orange-500/20 text-orange-200">
                          {mockup.alert2.tag1}
                        </div>
                        <div className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-indigo-500/10 border border-indigo-500/20 text-indigo-200">
                          {mockup.alert2.tag2}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-xl bg-teal-500/15 border border-teal-500/20 text-teal-200">
                          <User size={18} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-white/60">{mockup.alert3.label}</div>
                          <div className="text-sm font-semibold text-white/90 mt-1">{mockup.alert3.title}</div>
                          <div className="mt-2 pl-3 border-l border-white/15 text-sm text-white/80">
                            {mockup.alert3.desc}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 flex items-center gap-2">
                        <div className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-teal-500/10 border border-teal-500/20 text-teal-200">
                          {mockup.alert3.tag1}
                        </div>
                        <div className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white/5 border border-white/10 text-white/70">
                          {mockup.alert3.tag2}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* 3. Entregables */}
          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('sdi.deliverables_ext.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {deliverables.map((item, i) => {
                const Icon = [CheckCircle, Database, Workflow, FileText, MonitorPlay, ListTree][i] || CheckCircle;
                const iconClass = ['text-green-500', 'text-blue-500', 'text-purple-500', 'text-yellow-500', 'text-red-500', 'text-indigo-500'][i] || 'text-gray-500';
                return (
                  <div key={item.title || i} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <Icon className={`${iconClass} mb-4`} size={32} />
                    <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* 4. Tabla Comparativa */}
          <section className="mb-24">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{serviceLevels.title}</h2>
            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-lg">
              <table className="min-w-full bg-white">
                <thead>
                  <tr className="bg-gray-900 text-white">
                    <th className="py-4 px-6 text-left">{serviceLevels.columns?.feature}</th>
                    <th className="py-4 px-6 text-center bg-gray-800">{serviceLevels.columns?.basic}</th>
                    <th className="py-4 px-6 text-center bg-blue-900">{serviceLevels.columns?.standard}</th>
                    <th className="py-4 px-6 text-center bg-purple-900">{serviceLevels.columns?.professional}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr className="hover:bg-gray-50">
                    <td className="py-4 px-6 font-medium text-gray-900">{serviceLevels.rows?.focus?.label}</td>
                    <td className="py-4 px-6 text-center text-gray-600">{serviceLevels.rows?.focus?.basic}</td>
                    <td className="py-4 px-6 text-center text-gray-600 font-medium">{serviceLevels.rows?.focus?.standard}</td>
                    <td className="py-4 px-6 text-center text-gray-600 font-bold">{serviceLevels.rows?.focus?.professional}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="py-4 px-6 font-medium text-gray-900">{serviceLevels.rows?.duration?.label}</td>
                    <td className="py-4 px-6 text-center text-gray-600">{serviceLevels.rows?.duration?.basic}</td>
                    <td className="py-4 px-6 text-center text-gray-600">{serviceLevels.rows?.duration?.standard}</td>
                    <td className="py-4 px-6 text-center text-gray-600">{serviceLevels.rows?.duration?.professional}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="py-4 px-6 font-medium text-gray-900">{serviceLevels.rows?.diagram?.label}</td>
                    <td className="py-4 px-6 text-center text-gray-600">{serviceLevels.rows?.diagram?.basic}</td>
                    <td className="py-4 px-6 text-center text-gray-600">{serviceLevels.rows?.diagram?.standard}</td>
                    <td className="py-4 px-6 text-center text-gray-600 font-bold text-purple-600">{serviceLevels.rows?.diagram?.professional}</td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="py-4 px-6 font-medium text-gray-900">{serviceLevels.rows?.guide?.label}</td>
                    <td className="py-4 px-6 text-center text-gray-400"><X size={20} className="mx-auto" /></td>
                    <td className="py-4 px-6 text-center text-green-600"><CheckCircle size={20} className="mx-auto" /></td>
                    <td className="py-4 px-6 text-center text-green-600"><CheckCircle size={20} className="mx-auto" /></td>
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="py-4 px-6 font-medium text-gray-900">{serviceLevels.rows?.microservices?.label}</td>
                    <td className="py-4 px-6 text-center text-gray-600">{serviceLevels.rows?.microservices?.basic}</td>
                    <td className="py-4 px-6 text-center text-gray-600">{serviceLevels.rows?.microservices?.standard}</td>
                    <td className="py-4 px-6 text-center text-gray-600 font-bold text-purple-600">{serviceLevels.rows?.microservices?.professional}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-gray-500 mt-4 text-center italic">
              {serviceLevels.footnote}
            </p>
          </section>

          {/* 5. Capacidades Técnicas */}
          <section className="mb-20">
            <h2 className="text-3xl font-bold text-center text-[#141414] mb-12">{t('sdi.tech_capabilities.title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {techCapabilities.map((item, i) => {
                const Icon = [LayoutGrid, BrainCircuit, Shield][i] || LayoutGrid;
                const styles = [
                  { card: 'from-blue-50 to-white border-blue-100', bubble: 'bg-blue-100', icon: 'text-blue-600' },
                  { card: 'from-purple-50 to-white border-purple-100', bubble: 'bg-purple-100', icon: 'text-purple-600' },
                  { card: 'from-green-50 to-white border-green-100', bubble: 'bg-green-100', icon: 'text-green-600' },
                ][i] || { card: 'from-gray-50 to-white border-gray-100', bubble: 'bg-gray-100', icon: 'text-gray-600' };
                return (
                  <div key={item.title || i} className={`bg-gradient-to-br ${styles.card} p-8 rounded-2xl border text-center`}>
                    <div className={`inline-block ${styles.bubble} p-4 rounded-full mb-6`}>
                      <Icon className={styles.icon} size={32} />
                    </div>
                    <h3 className="text-xl font-bold mb-4">{item.title}</h3>
                    <p className="text-gray-600">{item.desc}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* CTA Final */}
          <div className="bg-gray-900 rounded-3xl p-12 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10">
              <ParticlesBackground color="#ffffff" density={30} />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl font-bold mb-6">{t('sdi.final_cta.title')}</h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
                {t('sdi.final_cta.subtitle')}
              </p>
              <button
                onClick={openPopup}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-4 px-10 rounded-full transition transform hover:scale-105 shadow-lg"
              >
                {t('sdi.final_cta.btn')}
              </button>
            </div>
          </div>

          </div>
        </div>
      </main>
    </div>
  );
};

export default SdiPage;

